/**
 * As quatro perguntas de acesso para o controle que **não é um botão** — issue #408.
 *
 * `RouteActionButton` resolve o caso comum: o controle é um `<Button>` e o componente decide
 * sozinho se renderiza habilitado ou desabilitado com o motivo. Mas metade dos controles de
 * escrita medidos na issue não é botão — é ação de linha do `DataTable`, item de menu, ícone
 * dentro de uma célula. Esses recebem `disabled` e `tooltip` de fora, e ficariam de fora da
 * reforma se a única porta fosse um componente.
 *
 * `useControle` é a porta deles: devolve exatamente os dois campos que qualquer controle
 * precisa, já com a política aplicada. A tela não escreve condição nenhuma — se ela estiver
 * escrevendo `veredito.liberado ? ... : ...`, é sinal de que falta capacidade aqui, e não de
 * que aquela tela é especial.
 */

import { useLocation } from 'react-router-dom';

import { avaliar, avaliarAcao, type Exigencia, type Veredito } from './pode';
import { useManifesto } from '../stores/manifestoStore';
import type { Modulo } from '../types/manifesto';

/**
 * O veredito cru sobre uma tela de destino, com o motivo e a frase. Use quando o controle
 * precisa do **porquê** — para escolher um ícone, mudar um texto de bloco, decidir uma faixa.
 * Para habilitar/desabilitar com o motivo, que é o caso de quase todos, use `useControle`.
 *
 * Sem manifesto ainda carregado nada é liberado: oferecer o controle "por enquanto" é
 * exatamente o clique que termina em recusa.
 */
export function useVeredito(para: string, exige: Exigencia): Veredito {
  const { manifesto } = useManifesto();

  if (!manifesto) {
    return { liberado: false, motivo: 'carregando', explicacao: 'Carregando seu acesso...' };
  }
  return avaliar(para, manifesto, exige);
}

export interface ControleDeAcesso {
  readonly disabled: boolean;
  /** O rótulo quando dá para agir; o motivo do bloqueio quando não dá. */
  readonly tooltip: string;
}

/**
 * O que um controle bloqueado mostra: desabilitado, com o motivo no lugar do rótulo. É o
 * espelho de `RouteActionButton` para quem não é botão.
 *
 * Aqui só cabe desabilitar — sumir seria um terceiro campo, que este formato não tem de
 * propósito: é a política de `explicarEmVezDeSumir` escrita como tipo, e não como condição
 * que cada tela precisa lembrar de repetir.
 */
export function useControle(para: string, exige: Exigencia, rotulo: string): ControleDeAcesso {
  return comoControle(useVeredito(para, exige), rotulo);
}

/**
 * O rótulo entra no tooltip do controle bloqueado, e não só o motivo, porque em ícone o
 * tooltip **é** o nome acessível (`aria-label` do `DataTable`). Trocando um pelo outro, a
 * pessoa que navega por leitor de tela ouve "O módulo Segurança do Trabalho não está
 * contratado" e nunca fica sabendo qual ação era.
 */
function comoControle(veredito: Veredito, rotulo: string): ControleDeAcesso {
  if (veredito.liberado) return { disabled: false, tooltip: rotulo };
  return { disabled: true, tooltip: `${rotulo} — ${veredito.explicacao ?? 'sem acesso'}` };
}

/**
 * O controle que **age na própria tela** — cancelar um agendamento, enviar uma CAT, gerar um
 * lote de eventos. Não há tela de destino para perguntar por: quem responde é a permissão da
 * ação, mais o vínculo e o módulo da tela onde o controle está, que o gancho lê da rota atual.
 *
 * Por isso a tela não passa caminho nenhum: passar seria deixá-la escolher a resposta, que é
 * exatamente o que a primeira onda desfez.
 */
export function useAcao(
  permissao: string | readonly string[],
  rotulo: string,
  opcoes: { modulo?: Modulo } = {}
): ControleDeAcesso {
  const { manifesto } = useManifesto();
  const { pathname } = useLocation();

  if (!manifesto) return { disabled: true, tooltip: `${rotulo} — Carregando seu acesso...` };
  return comoControle(avaliarAcao(pathname, manifesto, { permissao, modulo: opcoes.modulo }), rotulo);
}
